import {
    Box,
    Text,
    Stack,
    Flex,
    useColorModeValue,
    Image,
    VStack,
    HStack,
    Badge,
    keyframes,
} from "@chakra-ui/react";
import { FaGraduationCap, FaUniversity } from "react-icons/fa";
import masaiLogo from "../Assets/masai-logo.svg";

const fadeInUp = keyframes`
    from { opacity: 0; transform: translateY(40px); }
    to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
    from { opacity: 0; transform: translateX(-50px); }
    to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
    from { opacity: 0; transform: translateX(50px); }
    to { opacity: 1; transform: translateX(0); }
`;

const pulseRing = keyframes`
    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(147, 51, 234, 0.7); }
    70% { transform: scale(1); box-shadow: 0 0 0 20px rgba(147, 51, 234, 0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(147, 51, 234, 0); }
`;

const glowPulse = keyframes`
    0%, 100% { filter: drop-shadow(0 0 5px rgba(147, 51, 234, 0.5)); }
    50% { filter: drop-shadow(0 0 20px rgba(147, 51, 234, 0.8)); }
`;

const float = keyframes`
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
`;

const shimmer = keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
`;

const Education = () => {
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const textColor = useColorModeValue("gray.600", "gray.300");
    const timelineColor = useColorModeValue("purple.500", "purple.400");
    const cardBg = useColorModeValue("white", "gray.700");
    const iconColor = useColorModeValue("purple.600", "purple.300");
    const institutionColor = useColorModeValue("gray.800", "white");
    const degreeColor = useColorModeValue("purple.600", "purple.300");
    const highlightsTitleColor = useColorModeValue("gray.700", "gray.200");
    const labelBg = useColorModeValue("blue.50", "blue.900");
    const labelBorderColor = useColorModeValue("gray.300", "gray.600");
    const timelineLineColor = useColorModeValue("purple.200", "purple.700");
    const timelineGradientStart = useColorModeValue("#9333ea", "#a855f7");
    const nodeBg = useColorModeValue("white", "gray.800");
    const iconBoxBg = useColorModeValue("purple.100", "purple.900");

    const educations = [
        {
            id: 1,
            degree: "Full-Stack Web Development (Full-Time)",
            institution: "Masai School",
            location: "Bengaluru",
            duration: "April 2024 - Nov 2024",
            type: "Certification",
            description: "Enrolled full-time in a MERN Full Stack Web Development course, gaining hands-on experience in MongoDB, Express.js, React.js, and Node.js.",
            logo: masaiLogo,
            highlights: ["MERN Stack", "Data Structures", "Algorithm", "Soft Skills"]
        },
        {
            id: 2,
            degree: "Master of Computer Applications (MCA)",
            institution: "Sinhgad Institute Of Management",
            location: "Pune, Maharashtra",
            duration: "2018 - 2021",
            type: "Post Graduation",
            description: "Completed post graduation with focus on advanced computer applications, software development, and database management systems.",
            logo: "http://cms.sinhgad.edu/SIM_Web_Assets/images/sinhgad-logo-colour-blackText.png",
            highlights: ["Software Engineering", "Database Management", "Web Technologies"]
        },
        {
            id: 3,
            degree: "BCA (Bachelor of Computer Applications)",
            institution: "G.S.Science, Arts & Commerce College",
            location: "Khamgaon, Maharashtra",
            duration: "2014 - 2017",
            type: "Graduation",
            description: "Completed Bachelor's degree in Computer Applications, building foundational knowledge in programming and computer science.",
            logo: "https://gsck.ac.in/img/gsclogom.png",
            highlights: ["Programming Fundamentals", "Computer Networks", "Operating Systems"]
        }
    ];

    return (
        <Box width="100%" m="auto" padding={["5", "5", "10", "20"]}>
            <Stack spacing={8} w={["100%"]} m="auto">
                <Text
                    textTransform={"uppercase"}
                    color={"white.400"}
                    border="1px solid"
                    borderColor={labelBorderColor}
                    fontWeight={600}
                    fontSize={"sm"}
                    bg={labelBg}
                    p={2}
                    alignSelf={"flex-start"}
                    rounded={"md"}
                    animation={`${slideInLeft} 0.6s ease-out`}
                    position="relative"
                    overflow="hidden"
                    _before={{
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                        animation: `${shimmer} 2s infinite`,
                    }}
                >
                    Education
                </Text>

                <VStack spacing={10} align="stretch" position="relative">
                    {/* Animated Timeline Line */}
                    <Box
                        position="absolute"
                        left={{ base: "28px", md: "44px" }}
                        top="80px"
                        bottom="0"
                        width="3px"
                        bg={timelineLineColor}
                        borderRadius="full"
                        overflow="hidden"
                    >
                        <Box
                            width="100%"
                            height="100%"
                            bg={`linear-gradient(180deg, ${timelineGradientStart} 0%, transparent 100%)`}
                            animation={`${glowPulse} 2s ease-in-out infinite`}
                        />
                    </Box>

                    {educations.map((edu, index) => (
                        <Flex
                            key={edu.id}
                            direction={{ base: "column", md: "row" }}
                            gap={6}
                            position="relative"
                        >
                            {/* Timeline Node with Enhanced Animation */}
                            <Box
                                position="relative"
                                zIndex={2}
                                animation={`${slideInLeft} 0.8s ease-out ${index * 0.2}s both`}
                            >
                                <Flex
                                    w={{ base: "56px", md: "88px" }}
                                    h={{ base: "56px", md: "88px" }}
                                    align={"center"}
                                    justify={"center"}
                                    rounded={"full"}
                                    bg={nodeBg}
                                    boxShadow="0 0 30px rgba(147, 51, 234, 0.3)"
                                    border="3px solid"
                                    borderColor={timelineColor}
                                    position="relative"
                                    animation={`${pulseRing} 2s ease-out ${index * 0.3}s infinite`}
                                    transition="all 0.3s ease"
                                    _hover={{
                                        transform: "scale(1.1)",
                                        boxShadow: "0 0 40px rgba(147, 51, 234, 0.5)",
                                    }}
                                >
                                    {edu.logo ? (
                                        <Box
                                            w="100%"
                                            h="100%"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            bg="white"
                                            p={2}
                                            borderRadius="full"
                                            boxShadow="inner"
                                        >
                                            <Image 
                                                src={edu.logo} 
                                                alt={edu.institution}
                                                objectFit="contain"
                                                maxW="100%" 
                                                maxH="100%"
                                            />
                                        </Box>
                                    ) : (
                                        <FaGraduationCap size={28} color={timelineColor} />
                                    )}
                                </Flex>
                                {/* Year Badge */}
                                <Badge
                                    position="absolute"
                                    top="-8px"
                                    right="-20px"
                                    colorScheme="purple"
                                    fontSize="xs"
                                    px={2}
                                    py={0.5}
                                    borderRadius="full"
                                    animation={`${float} 3s ease-in-out ${index * 0.2}s infinite`}
                                >
                                    {edu.duration.split("-")[1].trim()}
                                </Badge>
                            </Box>

                            {/* Education Card with Enhanced Effects */}
                            <Box
                                flex={1}
                                bg={cardBg}
                                p={{ base: 6, md: 8 }}
                                borderRadius="2xl"
                                borderWidth="1px"
                                borderColor={borderColor}
                                boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                                position="relative"
                                overflow="hidden"
                                animation={`${slideInRight} 0.8s ease-out ${index * 0.2}s both`}
                                transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                                _before={{
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "4px",
                                    height: "100%",
                                    bg: "purple.500",
                                    transform: "scaleY(0)",
                                    transition: "transform 0.4s ease",
                                }}
                                _hover={{
                                    transform: "translateY(-8px) translateX(5px)",
                                    boxShadow: "0 20px 40px rgba(147, 51, 234, 0.15)",
                                    borderColor: "purple.300",
                                    _before: {
                                        transform: "scaleY(1)",
                                    }
                                }}
                            >
                                {/* Decorative Background Element */}
                                <Box
                                    position="absolute"
                                    top="-50%"
                                    right="-20%"
                                    width="200px"
                                    height="200px"
                                    bg="purple.50"
                                    borderRadius="full"
                                    opacity={0.5}
                                    filter="blur(40px)"
                                    pointerEvents="none"
                                />

                                <Stack spacing={4} position="relative" zIndex={1}>
                                    {/* Header */}
                                    <Flex
                                        justify="space-between"
                                        align={{ base: "flex-start", md: "center" }}
                                        direction={{ base: "column", md: "row" }}
                                        gap={2}
                                    >
                                        <HStack spacing={3}>
                                            <Box
                                                p={2}
                                                bg={iconBoxBg}
                                                borderRadius="lg"
                                                animation={`${float} 4s ease-in-out infinite`}
                                            >
                                                <FaUniversity color={iconColor} size={18} />
                                            </Box>
                                            <Text
                                                fontWeight={700}
                                                fontSize={{ base: "lg", md: "xl" }}
                                                color={institutionColor}
                                            >
                                                {edu.institution}
                                            </Text>
                                        </HStack>
                                        <Badge
                                            colorScheme="purple"
                                            fontSize="sm"
                                            px={3}
                                            py={1}
                                            borderRadius="full"
                                            textTransform="uppercase"
                                            letterSpacing="wide"
                                            boxShadow="0 2px 10px rgba(147, 51, 234, 0.3)"
                                        >
                                            {edu.type}
                                        </Badge>
                                    </Flex>

                                    {/* Degree & Duration */}
                                    <Box>
                                        <Text
                                            fontWeight={600}
                                            fontSize={{ base: "lg", md: "xl" }}
                                            color={degreeColor}
                                            display="flex"
                                            alignItems="center"
                                            gap={2}
                                        >
                                            <Box 
                                                w={2} 
                                                h={2} 
                                                bg="purple.500" 
                                                borderRadius="full"
                                                animation={`${pulseRing} 2s infinite`}
                                            />
                                            {edu.degree}
                                        </Text>
                                        <Text
                                            fontSize="sm"
                                            color={textColor}
                                            mt={2}
                                            display="flex"
                                            alignItems="center"
                                            gap={2}
                                        >
                                            <Box as="span" fontSize="lg">📍</Box>
                                            {edu.location} 
                                            <Box as="span" mx={2} color="purple.300">|</Box>
                                            <Box as="span" fontSize="lg">📅</Box>
                                            {edu.duration}
                                        </Text>
                                    </Box>

                                    {/* Description */}
                                    <Text
                                        color={textColor}
                                        fontSize={{ base: "md", md: "lg" }}
                                        lineHeight="tall"
                                    >
                                        {edu.description}
                                    </Text>

                                    {/* Highlights */}
                                    <Box pt={2}>
                                        <Text
                                            fontWeight={600}
                                            fontSize="md"
                                            mb={3}
                                            color={highlightsTitleColor}
                                        >
                                            Key Highlights:
                                        </Text>
                                        <Flex wrap="wrap" gap={2}>
                                            {edu.highlights.map((highlight, idx) => (
                                                <Badge
                                                    key={idx}
                                                    colorScheme="purple"
                                                    variant="subtle"
                                                    fontSize="sm"
                                                    px={4}
                                                    py={1.5}
                                                    borderRadius="full"
                                                    cursor="default"
                                                    transition="all 0.3s ease"
                                                    animation={`${fadeInUp} 0.5s ease-out ${0.5 + idx * 0.1}s both`}
                                                    _hover={{
                                                        transform: "scale(1.1) rotate(-2deg)",
                                                        boxShadow: "0 4px 12px rgba(147, 51, 234, 0.3)",
                                                        bg: "purple.500",
                                                        color: "white"
                                                    }}
                                                >
                                                    {highlight}
                                                </Badge>
                                            ))}
                                        </Flex>
                                    </Box>
                                </Stack>
                            </Box>
                        </Flex>
                    ))}
                </VStack>
            </Stack>
        </Box>
    );
};

export default Education;
